import { useCallback, useEffect } from 'react';
import { useMap } from 'react-map-gl';
import type { Geometry, Polygon, Position } from 'geojson';
import bbox from '@turf/bbox';
import PolygonArea from 'components/map/polygon-area';
import { QuarteredPolygon } from 'components/map/quartered-polygon';
import { DrawPolygonsControl } from 'components/query-map/draw-polygons-control';
import {
  MapPickControl,
  type SurveyAreaCandidate,
} from 'components/query-map/map-pick-control';
import { SurveyAreasLayer } from 'components/query-map/survey-areas-layer';
import type { PickedSurveyArea } from 'components/query-map/map-workspace-state';
import {
  snapPolygonsToSurveyGrid,
  snapPositionToSurveyGrid,
  SNAP_LAYER_IDS,
  SNAP_LAYER_PREFIXES,
} from 'components/query-map/snap-to-survey-grid';
import { circleFromPolygon, EMPTY_PICKED_AREAS, PREVIEW_COLOR } from './utils';
import { DrawCircleControl } from './draw-circle-control';
import type { DrawShape } from './create-area-types';

/** Room left around a survey pick the map moves to show. */
const SURVEY_FIT_PADDING_PX = 64;

/** How long that move takes, quick enough not to hold up the next pick. */
const SURVEY_FIT_DURATION_MS = 800;

/** Stable default for the drawn set. */
const EMPTY_POLYGONS: Polygon[] = [];

export interface CreateAreaMapLayersProps {
  /**
   * Polygons the user has drawn for a new area. Omit `onDrawnPolygonsChange` to
   * leave drawing off.
   */
  drawnPolygons?: Polygon[];
  /** Fires with the full drawn set on every add / edit / delete. */
  onDrawnPolygonsChange?: (polygons: Polygon[]) => void;
  /** Arms the next polygon — driven by the panel's "Draw polygon" button. */
  drawing?: boolean;
  /** Reports the map's live drawing state so the panel button can reset. */
  onDrawingChange?: (drawing: boolean) => void;
  /** Polygon to open for vertex editing, driven by the panel's Edit action. */
  editingIndex?: number | null;
  /** Fires as the map itself starts or ends an edit. */
  onEditingIndexChange?: (index: number | null) => void;
  /** The shape the panel's inputs would produce, as the backend derives it. */
  previewGeometry?: Geometry | null;
  /** The colour the panel would save the area with; null falls back to blue. */
  previewColor?: string | null;
  /** Set while a unit click means something. */
  onUnitClick?: ((developmentAreaId: number) => void) | null;
  /** Set while survey rows are on screen to receive a pick. */
  onSurveyAreaPick?: ((candidates: SurveyAreaCandidate[]) => void) | null;
  /** Survey areas already in those rows, drawn as picked. */
  pickedSurveyAreas?: PickedSurveyArea[];
  /** Pulls drawn and edited vertices onto nearby section lines. */
  snapToGrid?: boolean;
  /**
   * What the drawn method puts down next (Epic 2). In circle mode the circle
   * control takes the map's clicks (drawing when armed, re-editing drawn circles
   * when not) and the polygon control is not mounted, so neither gesture can
   * start the other's shape.
   */
  drawShape?: DrawShape;
  /**
   * Rides on the map move that brings a survey pick into view, for a host whose
   * own move handlers need to tell it apart (Map Query skips its extent search).
   */
  fitEventData?: Record<string, unknown>;
}

/**
 * The Create New Area panel's side of a map: survey picking, the drawn shapes, the
 * derived preview, and the drawing control itself. Mount it as a child of `<Map>`,
 * after the host's own layers, so the shapes under construction draw on top.
 *
 * @param {CreateAreaMapLayersProps} props - The panel's map state.
 * @return {JSX.Element} The layers and controls.
 */
export function CreateAreaMapLayers({
  drawnPolygons = EMPTY_POLYGONS,
  onDrawnPolygonsChange,
  drawing = false,
  onDrawingChange,
  editingIndex,
  onEditingIndexChange,
  previewGeometry,
  previewColor = null,
  onUnitClick,
  onSurveyAreaPick,
  pickedSurveyAreas = EMPTY_PICKED_AREAS,
  snapToGrid = false,
  drawShape = 'polygon',
  fitEventData,
}: CreateAreaMapLayersProps): JSX.Element {
  const shapeColor = previewColor ?? PREVIEW_COLOR;
  // In circle mode the circle control owns the map's clicks: armed it draws a
  // new circle, unarmed it lets a drawn one be picked up and edited. A polygon
  // opened for vertex editing hands the map to the polygon control instead.
  const circleMode =
    drawShape === 'circle' &&
    (editingIndex === null || editingIndex === undefined);
  const { current: mapRef } = useMap();

  // Brings the survey build into view when it isn't already, so an area picked
  // from the dropdown shows where it is. Follows the preview, the one shape every
  // pick has (a dropdown pick carries no geometry of its own), so quarter calls
  // count too. A pick already in full view, as a clicked section usually is,
  // leaves the map where it is; one cut off at the edge is brought fully in.
  const surveyPreview = onSurveyAreaPick ? previewGeometry : null;
  useEffect(() => {
    if (!mapRef || !surveyPreview) return;
    const [west, south, east, north] = bbox(surveyPreview);
    const view = mapRef.getBounds();
    if (view?.contains([west, south]) && view.contains([east, north])) return;
    mapRef.fitBounds(
      [
        [west, south],
        [east, north],
      ],
      { padding: SURVEY_FIT_PADDING_PX, duration: SURVEY_FIT_DURATION_MS },
      fitEventData
    );
  }, [mapRef, surveyPreview, fitEventData]);

  /**
   * Takes the drawn set from the map, snapping its vertices onto the survey grid
   * when the panel asked for that — here rather than in the draw control, because it
   * needs the grid layer this component mounts. Circles are left as drawn: pulling
   * their ring onto section lines would bend them out of being circles.
   */
  const handleDrawnPolygonsChange = useCallback(
    (polygons: Polygon[]) => {
      if (!onDrawnPolygonsChange) {
        return;
      }
      if (!snapToGrid) {
        onDrawnPolygonsChange(polygons);
        return;
      }
      const snapped = snapPolygonsToSurveyGrid(mapRef ?? null, polygons, {
        layerIds: SNAP_LAYER_IDS,
        layerPrefixes: SNAP_LAYER_PREFIXES,
      });
      onDrawnPolygonsChange(
        polygons.map((polygon, index) =>
          circleFromPolygon(polygon) ? polygon : snapped[index]
        )
      );
    },
    [onDrawnPolygonsChange, snapToGrid, mapRef]
  );

  /**
   * The same pull, applied to one vertex as it is placed, so the line visibly
   * sticks to the grid while drawing rather than only once the shape is closed.
   */
  const snapPosition = useCallback(
    (position: Position) => {
      if (!mapRef) {
        return position;
      }
      return snapPositionToSurveyGrid(mapRef, position, {
        layerIds: SNAP_LAYER_IDS,
        layerPrefixes: SNAP_LAYER_PREFIXES,
      });
    },
    [mapRef]
  );

  return (
    <>
      {/*
        Also mounted for snapping, which hit-tests this layer — a layer that
        isn't rendered has no features to query, and the faint fill doubles as
        a view of the grid being snapped to.
      */}
      {(!!onSurveyAreaPick || snapToGrid) && (
        // The wash only paints while picking; mounted for snapping alone the
        // layer stays invisible — it is queried, not looked at. Rows carved by
        // quarter calls are excluded: their carve is drawn below instead, so a
        // `W2` pick doesn't sit under a whole-section highlight.
        <SurveyAreasLayer
          pickedIds={pickedSurveyAreas
            .filter((area) => area.quarterCalls.length === 0)
            .map((area) => area.location.id)}
          showFill={!!onSurveyAreaPick}
        />
      )}
      {/*
        Quarter-called rows highlight only their carve, the way the Development
        Areas wizard does — same `QuarteredPolygon`, same quartered-geographies
        derivation the saved area will use.
      */}
      {pickedSurveyAreas
        .filter((area) => area.quarterCalls.length > 0)
        .map((area) => (
          <QuarteredPolygon
            key={`picked-${area.location.id}-${area.quarterCalls.join('+')}`}
            id={`picked-${area.location.id}`}
            location={area.location}
            quarterCalls={area.quarterCalls}
            color={PREVIEW_COLOR}
          />
        ))}
      {(!!onUnitClick || !!onSurveyAreaPick) && (
        <MapPickControl
          onPickUnit={onUnitClick}
          onPickSurveyArea={onSurveyAreaPick}
        />
      )}
      {/*
        The drawn shapes, filled in the area's colour. MapboxDraw paints only an
        outline — its fill is transparent because it styles an editing session,
        not a result — and the border is left to it, so the two don't fight.
      */}
      {drawnPolygons.map((polygon, index) => (
        <PolygonArea
          // eslint-disable-next-line @eslint-react/no-array-index-key -- the set has no ids, and a key that followed the coordinates would remount the fill on every vertex drag.
          key={`drawn-${index}`}
          id={`drawn-${index}`}
          geometry={polygon}
          color={shapeColor}
          showBorder={false}
        />
      ))}
      {/*
        Drawn last so the derived shape reads on top of its sources — and only
        when nothing was drawn, since a drawn area is already filled above and
        the preview of it is the same shape.
      */}
      {previewGeometry && drawnPolygons.length === 0 && (
        <PolygonArea
          id="area-preview"
          geometry={previewGeometry}
          color={shapeColor}
        />
      )}
      {/*
        Drawing is only wired up while the panel asks for it, so the host map stays
        a plain map the rest of the time. No MapboxDraw toolbar: the panel's "Draw
        polygon" button arms drawing and each polygon card deletes its own shape.
      */}
      {onDrawnPolygonsChange && circleMode && (
        <DrawCircleControl
          polygons={drawnPolygons}
          onPolygonsChange={onDrawnPolygonsChange}
          armed={drawing}
          // One shape per Draw press, as with a polygon: placing or cancelling
          // the circle hands the map back.
          onDone={() => onDrawingChange?.(false)}
        />
      )}
      {onDrawnPolygonsChange && !circleMode && (
        <DrawPolygonsControl
          polygons={drawnPolygons}
          onChange={handleDrawnPolygonsChange}
          controls={{ polygon: false, trash: false }}
          drawing={drawing}
          onDrawingChange={onDrawingChange}
          editingIndex={editingIndex}
          onEditingIndexChange={onEditingIndexChange}
          snapPosition={snapToGrid ? snapPosition : undefined}
          // These polygons are the area being authored, so a drag has to be asked
          // for: the shape holds still until its card puts it into edit.
          lockUnlessEditing
        />
      )}
    </>
  );
}
